import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { DialogsService } from '../../../../../core/services/dialog.service';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { PayoutDAO } from '../../../../../core/services/dao/payoutDAO';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getCurrentVesselRates } from '../../edit/edit.component';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import { VesselRateCalculationService } from '../../../../../core/services/vessel-rate-calculation-service';
import { VesselDAO } from '../../../../../core/services/dao/vesselDAO';
import { VesselRateDAO } from '../../../../../core/services/dao/vesselRateDAO';
import { AppMessagingService } from '../../../../../core/services/app-messaging.service';
import { ChickenTestDialogService } from '../../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { ConstantMan } from '../../../../../core/services/constantMan';
import { getLastPaymentType } from '../manage-charter.component';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { Router } from '@angular/router';
import { CaptainManageDetails } from 'anchor-shared/lib/model/entity/captainManageDetails';
import { CaptainManageDetailsDAO } from '../../../../../core/services/dao/captainManageDetailsDAO';
import { WaterBodyDAO } from '../../../../../core/services/dao/waterBodyDAO';
import { NumberFormatterService } from '../../../../../core/services/number.formatter.service';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { AdditionalCosts } from '../../../../../core/models/interface/additional-cost';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { SubPayoutDAO } from '../../../../../core/services/dao/subPayoutDAO';
import { SubPayout } from 'anchor-shared/lib/model/entity/subPayout';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { MatDialog } from '@angular/material/dialog';
import { HoldPayoutsModalComponent } from './hold-payouts-modal/hold-payouts-modal.component';
import { StripePaymentDAO } from '../../../../../core/services/dao/stripePaymentDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { environment } from '../../../../../environments/environment';
import { ArrayParsingUtils } from '../../../../../core/services/array-parsing-utils';
import {
  getAddOnsTotal,
  getAddOnsTotalFromPricingMetaData,
} from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { AddOn } from 'anchor-shared/lib/model/entity/addOn';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';
import { roundToNearestCent } from 'anchor-shared/lib/service/utils/helper';
import { UserDAO } from '../../../../../core/services/dao/userDAO';

@Component({
  selector: 'app-reconciliation-and-payout',
  templateUrl: './reconciliation-and-payout.component.html',
  styleUrls: ['./reconciliation-and-payout.component.scss'],
})
export class ReconciliationAndPayoutComponent implements OnInit, OnDestroy {
  @Input() charter: Charter;
  @Input() vesselRates: VesselRate[];
  @Input() additionalCosts: AdditionalCosts;
  @Input() reconciliationPayment: Payment;
  @Input() anchorProfit: number;
  @Input() additionalCharges: number;
  @Input() additionalHours: number;
  @Input() payments: Payment[];
  @Output() updateCharterEmitter = new EventEmitter<AdditionalCosts>();

  @Input()
  runFuelHoursAutoCalc: BehaviorSubject<boolean>;
  displayProgressSpinner = new BehaviorSubject<boolean>(false);
  costsForm: FormGroup;
  charterCompleted: boolean;
  captainPayouts: any;
  totalReconciliation: string;
  crewPayouts = [];
  miscPayouts = [];
  ownerPayouts: any;
  depositAmount = '';
  services: any[];
  discounts = [];
  payoutsPublished: boolean;
  additionalHoursOneHourTotal: number;
  additionalHoursCustomerTotal: number;
  reconciliationLoading = false;
  captainManageDetails: CaptainManageDetails;
  captainManageDetailsLoaded: boolean;
  fuelSaved = false;
  hoursSaved = false;
  charterOriginalAdditionalHours: number;
  charterOriginalAdditionalFuel: number;
  charterSavedFuelGallons: number;
  gallonsOfFuelUsed: number;
  savingFuelCosts: boolean;
  savingHours: boolean;
  payoutsGenerating: boolean = false;
  subs = [];
  charterPayoutRequest: CharterPayoutRequest;
  charterIsEditable: boolean;
  payouts = [];
  fuelIncludedInCharter: boolean;
  depositPayment: Payment;
  additionalCostsPayment: Partial<Payment> = {};
  fullPayment: Payment;
  addOns: AddOn[];
  remainderCost: number;
  remainderCostAbs: number;

  constructor(
    private charterDao: CharterDAO,
    private payoutDao: PayoutDAO,
    private subPayoutDao: SubPayoutDAO,
    private dialogService: DialogsService,
    private vesselRateCalcService: VesselRateCalculationService,
    private vesselDAO: VesselDAO,
    private vesselRateDAO: VesselRateDAO,
    private appMessagingService: AppMessagingService,
    private chickenTestDialog: ChickenTestDialogService,
    private snackBar: MatSnackBar,
    private captainManageDetailDao: CaptainManageDetailsDAO,
    private waterBodyDAO: WaterBodyDAO,
    private captainManageDetailsDAO: CaptainManageDetailsDAO,
    private formatter: NumberFormatterService,
    private router: Router,
    private dialog: MatDialog,
    private stripePaymentDao: StripePaymentDAO,
    private userDAO: UserDAO,
    private arrayParsingUtils: ArrayParsingUtils,
  ) {
    this.goToStripePaymentIntent = this.goToStripePaymentIntent.bind(this);
    this.processHold = this.processHold.bind(this);
  }

  ngOnInit() {
    if (
      this.charter.payouts &&
      this.charter.payouts.length &&
      this.charter.completed
    ) {
      this.charterIsEditable = false;
    } else {
      this.charterIsEditable = true;
    }
    this.initCostForm();
    this.initServiceInfo();
    this.initCaptainManageInfo();
    this.additionalCharges =
      this.getFuelCost() + this.additionalCosts.additionalCharges.cost;
    this.calculateAdditionalCostsTotals();
    this.initPaymentInfo();
    this.initAutoCalcWatcher();
    this.fuelIncludedInCharter = this.getFuelIsIncluded();
    this.totalReconciliation = this.getReconciliationTotal();
    if (this.payments) {
      this.setDepositPayment();
      this.fullPayment = this.payments.find(
        (payment) => payment.type === PaymentType.FULL,
      );
      this.additionalCostsPayment = this.payments.find(
        (payment) => payment.type === PaymentType.ADDITIONAL,
      );
    }
    this.addOns = this.charter.pricingMetaData.addOns.map((addOn) => {
      return { ...addOn, markupPrice: getAddOnsTotal([addOn], null, true) };
    });
    this.calculateRemainderCost();
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  markCompleted() {
    if (this?.depositPayment?.paymentIntentId) {
      //Deposit payment present
      this.captainManageDetailsDAO
        .adminGetCaptainManageDetails(this.charter.id)
        .subscribe((details) => {
          this.captainManageDetails = details;
          if (this.captainManageDetails?.refueledBoat) {
            if (!this.fuelIncludedInCharter) {
              // if fuel not included, process hold and mark complete
              this.charter.completed = true;
              this.charter.open = false;
              this.charter.reconciliationStatus = 'NOT SENT';
              this.collectFunds();
              this.postMarkComplete();
              return;
            }
          }
          const dialogSub = this.dialogService
            .MarkCharterComplete(
              details,
              this.depositAmount,
              this.depositPayment,
            )
            .subscribe((data) => {
              if (data?.shouldComplete) {
                this.charter.completed = true;
                this.charter.open = false;
                this.charter.reconciliationStatus = 'NOT SENT';

                const {
                  customerDamagedBoat,
                  hasAdditionalHours,
                  refueledBoat,
                } = details || {};
                const hasCaptureDefiningDetail =
                  customerDamagedBoat ||
                  hasAdditionalHours ||
                  (refueledBoat && !this.fuelIncludedInCharter);

                if (
                  customerDamagedBoat ||
                  data.shouldProcessHold ||
                  hasCaptureDefiningDetail
                ) {
                  // collect funds
                  this.collectFunds();
                } else {
                  // release funds
                  const releaseSub = this.stripePaymentDao
                    .cancelPaymentIntent(
                      this.depositPayment.paymentIntentId,
                      true,
                    )
                    .subscribe((fundsReleased) => {
                      if (fundsReleased) {
                        this.depositPayment.refunded = true;
                        this.depositPayment.refundAmount =
                          this.depositPayment.amount;
                        this.appMessagingService.openSnack(
                          'Security deposit funds successfully released!',
                        );
                        this.calculateRemainderCost();
                      }
                    });
                  this.subs.push(releaseSub);
                }

                this.postMarkComplete();
              }
            });
          this.subs.push(dialogSub);
        });
    } else {
      // No deposit payment
      this.charter.completed = true;
      this.charter.open = false;
      this.charter.reconciliationStatus = 'NOT SENT';

      const charterUpdateSub = this.charterDao
        .updateFromPartial(this.charter.id, {
          completed: this.charter.completed,
          open: this.charter.open,
          reconciliationStatus: this.charter.reconciliationStatus,
        })
        .subscribe((res) => {
          this.charterCompleted = true;
          this.updateSystemPayoutNotes();
        });
      this.subs.push(charterUpdateSub);
    }
  }

  setDepositPayment() {
    const depositPayments = this.payments.filter(
      (payment) => payment.type === PaymentType.DEPOSIT,
    );
    if (depositPayments.length > 1) {
      this.depositPayment = depositPayments.find((payment) => {
        if (parseFloat(payment.amount) !== parseFloat(payment.refundAmount)) {
          return true;
        } else {
          return false;
        }
      });
    } else {
      this.depositPayment = depositPayments[0];
    }
  }

  collectFunds() {
    const userSub = this.userDAO
      .getById(this.charter.userId)
      .subscribe((user) => {
        if (this?.depositPayment?.paymentIntentId) {
          const captureSub = this.stripePaymentDao
            .captureFunds(
              this.depositPayment.paymentIntentId,
              user.stripeCustomerId,
            )
            .subscribe((captureResponse) => {
              if (!captureResponse?.decline_code) {
                this.appMessagingService.openSnack(
                  'Security deposit funds successfully captured!',
                );
                if (!this.depositPayment.transactions) {
                  this.depositPayment.transactions = {};
                }
                this.depositPayment.transactions.status = 'succeeded';
              } else {
                // open failure modal
                const failureSub = this.dialogService
                  .HoldProcessingFailure(
                    captureResponse.decline_code,
                    this.charter.userId,
                    this.depositAmount,
                    this.depositPayment.paymentIntentId,
                  )
                  .subscribe((sendEmail) => {
                    if (!this.depositPayment.transactions) {
                      this.depositPayment.transactions = {};
                    }
                    this.depositPayment.transactions.status = 'failed';
                    this.depositPayment.transactions.decline_code =
                      captureResponse.decline_code;
                  });
                this.subs.push(failureSub);
              }
            });
          this.subs.push(captureSub);
        }
      });
    this.subs.push(userSub);
  }

  postMarkComplete() {
    if (this.payments) {
      this.setDepositPayment();
      this.fullPayment = this.payments.filter(
        (payment) => payment.type === PaymentType.FULL,
      )[0];
    }

    const charterUpdateSub = this.charterDao
      .updateFromPartial(this.charter.id, {
        completed: this.charter.completed,
        open: this.charter.open,
        reconciliationStatus: this.charter.reconciliationStatus,
      })
      .subscribe((res) => {
        this.charterCompleted = true;
        this.updateSystemPayoutNotes();
      });
    this.subs.push(charterUpdateSub);
  }

  getReconciliationPayout() {
    this.totalReconciliation = this.getReconciliationTotal();
    return this.totalReconciliation;
  }

  calculateFuelCosts() {
    this.savingFuelCosts = true;
    if (!this.gallonsOfFuelUsed) {
      this.gallonsOfFuelUsed = 0;
    }
    if (!this.fuelIncludedInCharter) {
      let gallons = this.gallonsOfFuelUsed;
      if (!gallons) {
        gallons = 0;
      }
      this.additionalCosts.fuelCosts.cost = String(
        gallons * this.charter.fuelCost,
      );
      this.charterSavedFuelGallons = this.gallonsOfFuelUsed;
      this.additionalCosts.fuelCosts.gallons = gallons;
      const updateAdditionalCostsSub = this.charterDao
        .updateFromPartial(this.charter.id, {
          additionalCosts: this.additionalCosts,
        })
        .subscribe();
      this.subs.push(updateAdditionalCostsSub);
      this.calculateAdditionalCostsTotals();
    } else {
      const gallons = this.gallonsOfFuelUsed;
      this.charterSavedFuelGallons = this.gallonsOfFuelUsed;
      this.additionalCosts.fuelCosts.gallons = gallons;
    }
    this.saveReconciliation();
    this.updateSystemPayoutNotes();
    this.savingFuelCosts = false;
    this.fuelSaved = true;
    this.calculateRemainderCost();
  }

  calculateAdditionalHours() {
    this.savingHours = true;
    this.additionalHoursOneHourTotal =
      this.vesselRateCalcService.calculateAdditionalOneHourListPriceTotal(
        this.charter.originalRates,
      ) + this.calculateCustomTimeBasedOneHourTotal();
    this.additionalHoursOneHourTotal =
      this.calcGratuityAndBookingForAdditionalHours(this.charter.originalRates);
    this.calculateAdditionalCostsTotals();
    this.incrementPayoutsAndAdditionalHoursCustomerTotal();
    // this.updateSystemPayoutNotes();
    this.savingHours = false;
    this.hoursSaved = true;

    this.calculateRemainderCost();
  }

  private calcGratuityAndBookingForAdditionalHours(
    originalRates: VesselRate[],
  ): number {
    let GratuityAndBookingAmount = 0;
    originalRates.forEach((rate) => {
      if (
        rate.rateType === RateType.Gratuity ||
        rate.rateType === RateType.BookingFee
      ) {
        GratuityAndBookingAmount += rate.hourlyRate;
      }
    });
    return GratuityAndBookingAmount + this.additionalHoursOneHourTotal;
  }

  calculateAdditionalCostsTotals(): void {
    let chargesTotal = 0.0;
    let fuelTotal: number;
    let customTotal = 0.0;
    if (this.savingFuelCosts) {
      fuelTotal = this.getFuelCost();
    } else {
      fuelTotal = Number(this.additionalCosts.fuelCosts.cost);
    }
    let grandTotal: number;

    this.additionalCosts.additionalCharges.individualCosts.forEach((cost) => {
      if (
        cost.description !== 'Additional Hours' &&
        cost.charterCompleteAtCreation
      ) {
        chargesTotal += parseFloat(cost.total);
      }
    });
    this.additionalCosts.additionalCharges.cost = parseFloat(
      chargesTotal.toFixed(2),
    );

    grandTotal = chargesTotal + fuelTotal;
    this.additionalCosts.total = chargesTotal.toFixed(2);
    this.additionalCharges = parseFloat(grandTotal.toFixed(2));
  }

  getFuelCost(): number {
    if (this.fuelIncludedInCharter) {
      return 0;
    }
    return (
      this.additionalCosts.fuelCosts.gallons * this.charter.boat.fuelBillingRate
    );
  }

  saveReconciliation() {
    this.charter.additionalCosts = JSON.stringify(this.additionalCosts);
    const updateCharterSub = this.charterDao
      .updateFromPartial(this.charter.id, {
        additionalCosts: this.charter.additionalCosts,
      })
      .subscribe(() => {
        this.openSnackbar('Additional charges saved');
        this.calculateRemainderCost();
      });
    this.subs.push(updateCharterSub);
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  updateSystemPayoutNotes() {
    let updatedPayouts = [];
    this.charter.payouts.forEach((payout) => {
      if (
        this.savingHours &&
        (payout.type === 'captain' ||
          payout.type === 'crew' ||
          payout.type === 'owner')
      ) {
        let durationNotes;
        if (this.charter.duration === 4) {
          durationNotes = 'Half day charter';
        } else if (this.charter.duration === 8) {
          durationNotes = 'Full day charter';
        } else {
          durationNotes = this.charter.duration + ' hour charter';
        }
        if (Number(this.additionalHours) === 0) {
          payout.systemNotes = durationNotes + '. ';
        } else if (this.additionalHours < 0) {
          payout.systemNotes =
            durationNotes + ', ' + this.additionalHours + ' hours lost. ';
        } else {
          payout.systemNotes =
            durationNotes +
            ', +' +
            this.additionalHours +
            ' additional hours. ';
        }
        updatedPayouts.push(payout);
      }
      if (
        this.savingFuelCosts &&
        payout.name === 'Fuel Included' &&
        this.gallonsOfFuelUsed
      ) {
        payout.systemNotes = this.gallonsOfFuelUsed + ' gallons of fuel used. ';
        updatedPayouts.push(payout);
      } else if (
        this.savingFuelCosts &&
        payout.name === 'Fuel Included' &&
        !this.gallonsOfFuelUsed
      ) {
        payout.systemNotes = 0 + ' gallons of fuel used.';
        updatedPayouts.push(payout);
      }
    });
    const updateNotesSub = this.payoutDao
      .updateMultiple(updatedPayouts)
      .subscribe((data) => {
        this.initPayouts(updatedPayouts, true);
      });
    this.subs.push(updateNotesSub);
  }

  initPayouts(payouts, updated?: boolean) {
    payouts.forEach((el) => {
      if (el.type === 'captain') {
        this.captainPayouts = el;
      } else if (el.type === 'owner') {
        this.ownerPayouts = el;
      } else if (el.type === 'crew') {
        this.crewPayouts.push(el);
      } else if (el.type === 'misc') {
        const foundPayoutIdx = this.miscPayouts.findIndex(
          (element) => element.name === el.name,
        );
        if (foundPayoutIdx > -1 && updated) {
          this.miscPayouts[foundPayoutIdx] = el;
        } else {
          this.miscPayouts.push(el);
        }
      }
      if (el.published) {
        this.payoutsPublished = true;
      }
    });
  }

  publishPayouts() {
    const reqBodyPayouts: Partial<Payout>[] =
      this.createPayoutsForUpdatePayoutRequest(this.charter.payouts);
    const publishSub = this.dialogService
      .PublishPayments()
      .subscribe((data) => {
        if (data) {
          let updatedSubPayouts: Partial<SubPayout>[] = [];
          reqBodyPayouts.forEach((payout) => {
            payout.published = true;
            if (payout.subPayouts) {
              payout.subPayouts.forEach((subPayout) => {
                updatedSubPayouts.push({
                  id: subPayout.id,
                  published: true,
                  processingFee: subPayout.processingFee,
                  actualPay: subPayout.actualPay,
                  netPay: null,
                });
              });
            }
            delete payout.subPayouts;
          });
          const updateSubPayoutsSub = this.subPayoutDao
            .updateMultiple(updatedSubPayouts)
            .subscribe();
          this.subs.push(updateSubPayoutsSub);
          const updatePayoutsSub = this.payoutDao
            .updateMultiplePayoutsByCharter(reqBodyPayouts)
            .subscribe(() => {
              this.payoutsPublished = true;
              this.openSnackbar('Payouts published');
            });
          this.subs.push(updatePayoutsSub);
        }
      });
    this.subs.push(publishSub);
  }

  calculateRemainderCost() {
    this.remainderCost = roundToNearestCent(
      this.charter.depositAmount -
        (this.getReconciliationAdditionalCosts() +
          this.getAdditionalHoursTotal() +
          this.getFuelCost()) -
        parseFloat(this.depositPayment?.refundAmount || '0'),
    );
    this.remainderCostAbs = Math.abs(this.remainderCost);
  }

  getReconciliationTotal() {
    const additionalCharges = this.getReconciliationAdditionalCosts();
    return additionalCharges.toFixed(2);
  }

  getReconciliationAdditionalCosts(): number {
    let totalCostsForRecon = 0;
    this.additionalCosts.additionalCharges.individualCosts.forEach((cost) => {
      if (cost.charterCompleteAtCreation) {
        totalCostsForRecon += parseFloat(cost.total);
      }
    });
    return totalCostsForRecon;
  }

  getAdditionalChargesNum(): number {
    return this.additionalCharges;
  }

  getAdditionalHoursTotal(): number {
    let additionalHoursIdx =
      this.additionalCosts.additionalCharges.individualCosts.findIndex(
        (obj) => obj.description === 'Additional Hours',
      );
    if (additionalHoursIdx > -1) {
      return parseFloat(
        this.additionalCosts.additionalCharges.individualCosts[
          additionalHoursIdx
        ].total,
      );
    }
    return 0;
  }

  incrementPayoutsAndAdditionalHoursCustomerTotal() {
    const vesselSub = this.vesselDAO
      .loadById(this.charter.vesselId)
      .subscribe((vessel) => {
        const ratesSub = this.vesselRateDAO
          .getByVesselId(vessel.id)
          .subscribe((rates) => {
            let vesselRates = getCurrentVesselRates(rates, this.charter);
            if (!this.additionalHours) {
              this.additionalHours = 0;
            }
            this.additionalHoursCustomerTotal = parseFloat(
              (this.additionalHoursOneHourTotal * this.additionalHours).toFixed(
                2,
              ),
            );
            let additionalHoursIdx =
              this.additionalCosts.additionalCharges.individualCosts.findIndex(
                (obj) => obj.description === 'Additional Hours',
              );
            if (additionalHoursIdx > -1) {
              this.additionalCosts.additionalCharges.individualCosts[
                additionalHoursIdx
              ].total = this.additionalHoursCustomerTotal.toFixed(2).toString();
              this.additionalCosts.additionalCharges.individualCosts[
                additionalHoursIdx
              ].additionalHours = this.additionalHours;
            } else {
              this.additionalCosts.additionalCharges.individualCosts.push({
                description: 'Additional Hours',
                total: this.additionalHoursCustomerTotal.toFixed(2).toString(),
                additionalHours: this.additionalHours,
                payeePay: null,
                payee: null,
              });
            }

            const updateSub = this.charterDao
              .updateFromPartial(this.charter.id, {
                additionalCosts: this.additionalCosts,
              })
              .subscribe();
            this.subs.push(updateSub);
            this.saveReconciliation();
          });
        this.subs.push(ratesSub);
      });
    this.subs.push(vesselSub);
  }

  addNewCost() {
    const dialogSub = this.dialogService
      .AddNewCostDialog(this.charter)
      .subscribe((data: AdditionalCosts) => {
        if (data) {
          this.additionalCosts = data;
          this.charter.additionalCosts = data;
          this.additionalCharges = data.additionalCharges.cost;
          this.calculateAdditionalCostsTotals();
          this.saveReconciliation();
          this.calculateRemainderCost();

          this.updateCharterEmitter.emit(this.additionalCosts);
        }
      });
    this.subs.push(dialogSub);
  }

  deleteAdditionalCost(cost: any) {
    const costToDeleteIndex =
      this.additionalCosts.additionalCharges.individualCosts.findIndex(
        (additionalCharge) => {
          return (
            additionalCharge.description === cost.description &&
            additionalCharge.total === cost.total
          );
        },
      );
    this.additionalCosts.additionalCharges.individualCosts.splice(
      costToDeleteIndex,
      1,
    );

    const originalRateIndex = this.charter.originalRates.findIndex((rate) => {
      return rate.name === cost.description;
    });
    if (originalRateIndex > 0) {
      this.charter.originalRates.splice(originalRateIndex, 1);
      this.vesselRates = this.charter.originalRates;
    }
    const updateCharterSub = this.charterDao
      .updateFromPartial(this.charter.id, {
        originalRates: this.charter.originalRates,
        additionalCosts: this.additionalCosts,
      })
      .subscribe(() => {
        this.calculateRemainderCost();
      });

    this.updateCharterEmitter.emit(this.additionalCosts);

    this.subs.push(updateCharterSub);
  }

  editAdditionalCost(cost: any) {
    const costPaid =
      !!this.depositPayment?.refunded ||
      (this.additionalCostsPayment?.paymentIntentId &&
        !this.additionalCostsPayment?.refunded);
    this.dialogService
      .AddNewCostDialog(this.charter, cost, costPaid)
      .subscribe((data) => {
        if (data) {
          this.additionalCosts = data[0];
          this.charter.additionalCosts = data[0];
          this.additionalCharges = data[0].additionalCharges.cost;
          this.charter.payouts = data[1];
          this.initPayouts(this.charter.payouts);
          this.calculateAdditionalCostsTotals();
          this.saveReconciliation();
          this.updateCharterEmitter.emit(this.additionalCosts);
        }
      });
  }

  sendReconciliationChickenTestClick(): void {
    this.totalReconciliation = this.getReconciliationTotal();
    this.chickenTestDialog
      .open(
        "You're about to send a reconciliation in the amount of: " +
          this.totalReconciliation,
        'Yes, send',
        "No, don't send",
      )
      .subscribe((userSelection) => {
        if (userSelection) {
          this.sendReconciliation();
        }
      });
  }

  sendReconciliation() {
    let paymentId;
    const totalRecValue = parseFloat(this.totalReconciliation);
    const charterDepositPayment = getLastPaymentType(
      this.charter,
      PaymentType.DEPOSIT,
    );
    if (
      charterDepositPayment &&
      Math.abs(totalRecValue) === this.charter.depositAmount
    ) {
      paymentId = charterDepositPayment.id;
    } else {
      this.charter.payments.forEach((el) => {
        if (
          el.type === PaymentType.FULL ||
          el.type === PaymentType.REMAINING ||
          el.type === PaymentType.DEPOSIT
        ) {
          if (el.paid) {
            paymentId = el.id;
          }
        }
      });
    }

    this.charter.additionalCosts = JSON.stringify(this.additionalCosts);
    this.charter.reconciliationStatus = 'SENT';
    this.reconciliationLoading = true;
    const updateCharterSub = this.charterDao
      .updateFromPartial(this.charter.id, {
        additionalCosts: this.charter.additionalCosts,
        reconciliationStatus: this.charter.reconciliationStatus,
      })
      .subscribe((res) => {
        const data = {
          paymentId,
          amount: this.totalReconciliation,
          fuelReconcile: this.getFuelCost(),
          additionalReconcile: this.additionalCosts.additionalCharges.cost,
        };
        const reconSub = this.charterDao
          .sendReconciliation(data)
          .subscribe((_data) => {
            if (_data.success) {
              this.openSnackbar('Charge Successful');
              this.reconciliationPayment = _data.payment;
            } else {
              this.openSnackbar('Charge Unsuccessful. Please try again');
            }
            this.reconciliationLoading = false;
          });
        this.subs.push(reconSub);
      });
    this.subs.push(updateCharterSub);
  }

  calculateVesselRateWithMarkup(rate: VesselRate): number {
    return this.vesselRateCalcService.calculateVesselRate(
      rate,
      this.getDurationType(this.charter.duration),
      this.charter.duration,
      true,
    );
  }

  private getDurationType(duration: number): 0 | 4 | 8 {
    if (duration === 4) {
      return 4;
    } else if (duration === 8) {
      return 8;
    } else {
      return 0;
    }
  }

  getAnchorFees(): number {
    let total = this.charter.charterBaseFee + this.charter.serviceTotal;
    if (this.charter.discountTotal) {
      total -= this.charter.discountTotal;
    }
    if (
      this.charter.pricingMetaData &&
      this.charter.pricingMetaData.promoCode
    ) {
      total -= this.charter.pricingMetaData.promoCode.promoDiscountAmount;
    }
    if (this.charter.pricingMetaData?.addOns) {
      total += getAddOnsTotalFromPricingMetaData(
        this.charter.pricingMetaData,
        null,
      );
    }
    return total;
  }

  totalAdditionalHours(): number {
    let additionalHoursIndex =
      this.additionalCosts.additionalCharges.individualCosts.findIndex(
        (obj) => obj.description === 'Additional Hours',
      );
    if (additionalHoursIndex > -1) {
      return parseFloat(
        this.additionalCosts.additionalCharges.individualCosts[
          additionalHoursIndex
        ].total,
      );
    } else {
      return 0;
    }
  }

  getCharterTotal(includePaidDeposit: boolean = false) {
    const total = this.getAnchorFees();
    const recon = this.getAdditionalChargesNum();
    let depositAmount = 0;
    if (includePaidDeposit) {
      if (this.depositPayment.paid) {
        depositAmount =
          parseFloat(this.depositPayment.amount) -
          parseFloat(this.depositPayment.refundAmount || '0');
      }
    }
    return (
      Number(total) +
      Number(recon) +
      Number(this.getAdditionalHoursTotal()) +
      depositAmount
    );
  }

  totalCosts(): number {
    return this.getActualTotalFromCharterPayouts();
  }

  getActualTotalFromCharterPayouts(): number {
    let total = 0;
    this.charter.payouts.forEach((payout) => {
      if (payout && payout.actualPay) {
        total += parseFloat(payout.actualPay)
          ? parseFloat(payout.actualPay)
          : 0;
      }
    });
    return total;
  }

  getAdjustedAnchorNet() {
    const total = this.getCharterTotal();
    return total - this.getActualTotalFromCharterPayouts();
  }

  getGrossMarginPercentage() {
    const net = this.getAdjustedAnchorNet();
    const total = this.getCharterTotal();
    return net / total;
  }

  savePayouts() {
    const reqBodyPayouts: Partial<Payout>[] =
      this.createPayoutsForUpdatePayoutRequest(this.charter.payouts);

    const updatePayoutSub = this.payoutDao
      .updateMultiplePayoutsByCharter(reqBodyPayouts)
      .subscribe(() => {
        this.openSnackbar('Payouts saved');
      });
    this.subs.push(updatePayoutSub);
  }

  private completeAutoCalcProcess(): void {
    this.displayProgressSpinner.next(false);
    this.clearAutoCalcQueryParam();
  }

  private clearAutoCalcQueryParam(): void {
    this.router.navigate(['manage', 'manage-charter', this.charter.id], {
      queryParams: {
        runFuelAddHours: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private initAutoCalcWatcher(): void {
    const runCalcSub = this.runFuelHoursAutoCalc.subscribe(
      async (shouldRunCalc) => {
        if (shouldRunCalc) {
          this.displayProgressSpinner.next(true);
          while (
            this.gallonsOfFuelUsed === undefined ||
            !this.captainManageDetailsLoaded
          ) {
            await this.delay(1000);
          }
          if (
            this.additionalHours &&
            this.additionalHours !== 0 &&
            this.gallonsOfFuelUsed &&
            this.gallonsOfFuelUsed > 0
          ) {
            this.calculateAdditionalHours();
            await this.delay(2000);
            this.calculateFuelCosts();
            await this.delay(2000);
            this.completeAutoCalcProcess();
          } else if (this.additionalHours && this.additionalHours !== 0) {
            this.calculateAdditionalHours();
            await this.delay(2000);
            this.completeAutoCalcProcess();
          } else if (this.gallonsOfFuelUsed && this.gallonsOfFuelUsed > 0) {
            this.calculateFuelCosts();
            await this.delay(2000);
            this.completeAutoCalcProcess();
          } else {
            this.completeAutoCalcProcess();
          }
        }
      },
    );
    this.subs.push(runCalcSub);
  }

  private initPaymentInfo(): void {
    this.charter.payments.forEach((payment) => {
      if (
        payment.type === PaymentType.RECONCILIATION ||
        payment.name === 'Post Charter Refund'
      ) {
        this.reconciliationPayment = payment;
      }
    });
  }

  private initCostForm(): void {
    this.costsForm = new FormGroup({
      fuel: new FormControl(
        {
          value: this.gallonsOfFuelUsed ? this.gallonsOfFuelUsed : '',
          disabled: !this.charterIsEditable,
        },
        [Validators.pattern(/^-?\d*\.?\d*$/)],
      ),
      additional: new FormControl(
        {
          value: this.additionalHours ? this.additionalHours : '',
          disabled: !this.charterIsEditable,
        },
        [Validators.pattern(/^-?\d*\.?\d*$/)],
      ),
    });
  }

  private initServiceInfo(): void {
    this.services = this.charter.services;

    let additionalHoursObj =
      this.additionalCosts.additionalCharges.individualCosts.find(
        (obj) => obj.description === 'Additional Hours',
      );

    this.services = this.charter.services;

    if (additionalHoursObj) {
      this.additionalHours = additionalHoursObj.additionalHours;
      this.charterOriginalAdditionalHours = additionalHoursObj.additionalHours;
    } else {
      this.additionalHours = 0;
      this.charterOriginalAdditionalHours = 0;
    }
    this.additionalHours = Number(this.additionalHours);
    this.additionalCharges = this.additionalCosts.additionalCharges.cost;

    this.additionalCosts.additionalCharges.individualCosts.some((item) => {
      if (item.description === 'Additional Hours') {
        this.additionalHoursCustomerTotal = parseFloat(item.total);
        return true;
      }
    });
  }

  private initCaptainManageInfo(): void {
    const detailsSub = this.captainManageDetailsDAO
      .adminGetCaptainManageDetails(this.charter.id)
      .subscribe((details) => {
        this.captainManageDetails = details;
        if (typeof this.captainManageDetails?.endingDamagePhotos === 'string') {
          this.captainManageDetails.endingDamagePhotos =
            this.arrayParsingUtils.parseArrayFromJsonString(
              this.captainManageDetails?.endingDamagePhotos,
            );
        }
        this.captainManageDetailsLoaded = true;
        if (this.captainManageDetails && !this.additionalHours) {
          this.additionalHours = this.captainManageDetails.additionalHours;
        }
        if (
          this.captainManageDetails &&
          !this.additionalCosts.fuelCosts.gallons
        ) {
          this.gallonsOfFuelUsed =
            this.captainManageDetails.startFuelGallons -
            this.captainManageDetails.endFuelGallons;
          this.charterSavedFuelGallons = this.gallonsOfFuelUsed;
        } else {
          this.gallonsOfFuelUsed = this.additionalCosts.fuelCosts.gallons;
          this.charterSavedFuelGallons = this.gallonsOfFuelUsed;
        }
        this.charterOriginalAdditionalFuel =
          this.additionalCosts.fuelCosts.gallons || 0;
      });
    this.subs.push(detailsSub);
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  generatePayoutsForCharter(finalizing?: boolean) {
    let chickenTestQuestion: string;
    let affirmativeText: string;
    let negativeText: string;
    if (finalizing) {
      chickenTestQuestion =
        'Are you sure you want to finalize this charter? ' +
        'Payouts will be generated at this point, so please ' +
        'make sure all of the charter information is correct.';
      affirmativeText = 'Yes, finalize';
      negativeText = "No, don't finalize";
    } else {
      chickenTestQuestion =
        'Are you sure you want to regenerate the payouts for this charter? ' +
        "Any edits you've made to the payouts will need to be redone.";
      affirmativeText = 'Yes, regenerate';
      negativeText = "No, don't regenerate";
    }

    this.chickenTestDialog
      .open(chickenTestQuestion, affirmativeText, negativeText)
      .subscribe((userSelection) => {
        if (userSelection) {
          this.payoutsGenerating = true;
          this.charterPayoutRequest = this.createCharterPayoutRequest();
          const payoutIdsToDelete: number[] = [];

          const payoutRequestSub = this.payoutDao
            .charterPayoutRequest(this.charterPayoutRequest)
            .subscribe((payouts) => {
              this.charter.payouts.forEach((existingPayout) => {
                let payoutHasPaidSplits;
                if (existingPayout) {
                  if (
                    existingPayout.subPayouts &&
                    existingPayout.subPayouts.length > 0
                  ) {
                    payoutHasPaidSplits = existingPayout.subPayouts.find(
                      (subpayout) => {
                        return (
                          subpayout.paid === true ||
                          subpayout.adminHold === true
                        );
                      },
                    );
                  }
                }
                if (
                  existingPayout.paid === false &&
                  existingPayout.adminHold === false &&
                  !payoutHasPaidSplits
                ) {
                  payoutIdsToDelete.push(existingPayout.id);
                } else {
                  const deletionIndex = payouts.findIndex((newPayout) => {
                    if (
                      existingPayout.type === 'owner' ||
                      existingPayout.type === 'captain'
                    ) {
                      return newPayout.type === existingPayout.type;
                    } else if (existingPayout.type === 'crew') {
                      return (
                        newPayout.type === existingPayout.type &&
                        newPayout.crewId === existingPayout.crewId
                      );
                    } else {
                      return (
                        newPayout.type === existingPayout.type &&
                        newPayout.name === existingPayout.name
                      );
                    }
                  });
                  payouts.splice(deletionIndex, 1);
                }
              });
              const deletePayoutsSub = this.payoutDao
                .deleteMultiple(payoutIdsToDelete)
                .subscribe((deletingPayoutsResults) => {
                  const createPayoutsSub = this.payoutDao
                    .createMultiple(payouts)
                    .subscribe((regeneratedPayouts) => {
                      this.charter.payouts = regeneratedPayouts.map(
                        (newPayout) => {
                          return {
                            ...newPayout,
                            splitInProgress: false,
                          };
                        },
                      );
                      this.payoutsGenerating = false;
                      this.charterIsEditable = false;
                      this.initCostForm();
                    });
                  this.subs.push(createPayoutsSub);
                });
              this.subs.push(deletePayoutsSub);
            });
          this.subs.push(payoutRequestSub);
        }
      });
  }

  createCharterPayoutRequest(): CharterPayoutRequest {
    let crewIds = [];
    const parsedCrew = this.formatter.parseArrayFromJson(this.charter.crew);
    if (parsedCrew && parsedCrew.length) {
      parsedCrew.forEach((crewMember) => {
        crewIds.push(crewMember.id);
      });
    }

    return {
      charterId: this.charter.id,
      date: this.charter.date,
      captainChangeId: this.charter.captain ? this.charter.captain.id : null,
      durationChangeValue: this.charter.duration,
      crewMemberIds: crewIds,
      createFromScratch: true,
    };
  }

  fuelHoursEditWarning() {
    const fuelChickenSub = this.chickenTestDialog
      .open(
        'If you edit these values, you will need to regenerate payouts. ' +
          'Any edits you have made to payouts will need to be redone. ' +
          'Do you wish to continue?',
        'Yes, continue',
        "No, don't continue",
      )
      .subscribe((userSelection) => {
        if (userSelection) {
          this.charterIsEditable = true;
          this.initCostForm();
        }
      });
    this.subs.push(fuelChickenSub);
  }

  private createPayoutsForUpdatePayoutRequest(
    payouts: Payout[],
  ): Partial<Payout>[] {
    const reqBodyPayouts: Partial<Payout>[] = [];
    payouts.forEach((payout) => {
      reqBodyPayouts.push({
        id: payout.id,
        payee: payout.payee,
        type: payout.type,
        manualPaymentType: payout.manualPaymentType,
        basePay: payout.basePay,
        actualPay: payout.actualPay,
        userId: payout.userId,
        ownerId: payout.ownerId,
        notes: payout.notes,
        systemNotes: payout.systemNotes,
        paid: payout.paid,
        // @ts-ignore
        manual: payout.manual,
        published: payout.published,
        processingFee: payout.processingFee,
        netPay: payout.netPay,
        subPayouts: payout.subPayouts,
        payeePaypalHandle: payout.payeePaypalHandle,
        name: payout.name,
        taxPercentage: payout.taxPercentage,
        markupPercentage: payout.markupPercentage,
        useDefaultCrewRate: payout.useDefaultCrewRate,
        crewId: payout.crewId,
        paymentId: payout.paymentId,
      });
    });
    return reqBodyPayouts;
  }

  private calculateCustomTimeBasedOneHourTotal(): number {
    let customTotal = 0.0;
    this.charter.originalRates.forEach((rate) => {
      if (
        rate.rateType === RateType.CustomTimeBased ||
        (this.fuelIncludedInCharter && rate.rateType === RateType.FuelIncluded)
      ) {
        customTotal +=
          this.vesselRateCalcService.calculateHourlyWithMarkupAndTax(rate);
      }
    });
    return customTotal;
  }

  getFuelIsIncluded(): boolean {
    if (this.charter.originalRates) {
      return this.charter.originalRates.some(
        (rate) => rate.rateType === RateType.FuelIncluded,
      );
    } else {
      return this.charter.boat.fuelIncluded;
    }
  }

  openHoldsModal() {
    this.dialog
      .open(HoldPayoutsModalComponent, {
        data: {
          payouts: this.charter.payouts,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.charter.payouts.forEach((payout) => {
            if (payout.subPayouts && payout.subPayouts.length) {
              payout.subPayouts.forEach((subPayout) => {
                result.updatedSubPayouts.forEach((updatedSubPayout) => {
                  if (subPayout.id === updatedSubPayout.id) {
                    subPayout.adminHold = !subPayout.adminHold;
                  }
                });
              });
            }
            result.updatedPayouts.forEach((updatedPayout) => {
              if (updatedPayout.id === payout.id) {
                payout.adminHold = !payout.adminHold;
              }
            });
          });
        }
      });
  }

  chargeAdditionalCosts() {
    this.dialogService
      .ChargeAdditionalCostsDialog(
        this.charter,
        this.getAdditionalHoursTotal(),
        this.getFuelCost(),
        this.getReconciliationAdditionalCosts(),
        parseFloat(this.depositPayment?.amount) -
          parseFloat(this.depositPayment?.refundAmount || '0'),
      )
      .subscribe((paymentIntent) => {
        if (paymentIntent) {
          this.additionalCostsPayment = {
            paymentIntentId: paymentIntent.id,
            amount: (parseFloat(paymentIntent.amount) / 100).toString(),
          };
          this.calculateRemainderCost();
        }
      });
  }

  refundAdditionalCosts() {
    const refundAmount =
      this.getReconciliationAdditionalCosts() +
      this.getAdditionalHoursTotal() +
      this.getFuelCost();
    this.dialogService
      .SecurityHoldPostprocessingDialog(
        this.depositPayment,
        true,
        null,
        refundAmount,
        this.fullPayment,
      )
      .subscribe((response) => {
        if (response) {
          this.fullPayment.refunded = true;
          this.appMessagingService.openSnack('Refund processed successfully!');
          this.calculateRemainderCost();
        }
      });
  }

  goToStripePaymentIntent(intentId) {
    let stripeUrl = `https://dashboard.stripe.com/payments/${intentId}`;
    if (!environment.production) {
      stripeUrl = `https://dashboard.stripe.com/test/payments/${intentId}`;
    }
    window.open(stripeUrl, '_blank');
  }

  processHold(amount) {
    let paymentIntentIdToRefund = this.depositPayment.paymentIntentId;
    this.stripePaymentDao
      .refundHold(paymentIntentIdToRefund, Number(amount))
      .subscribe((data) => {
        if (data) {
          this.depositPayment.refundAmount = amount;
          this.calculateRemainderCost();
          this.appMessagingService.openSnack('Payment refunded successfully!');
          this.calculateRemainderCost();
        } else {
          this.appMessagingService.openSnack(
            'An error occurred refunding the payment.',
          );
        }
      });
  }

  refundDepositValidator({ amount }) {
    if (Number(amount) > Number(this.depositPayment.amount)) {
      return {
        valid: false,
        error: 'Refund must be less than deposit amount.',
      };
    }
    if (amount < 0 || !amount) {
      return { valid: false, error: 'Please provide a value greater than 0.' };
    }
    return { valid: true };
  }

  refundDeposit(refundRemainder = false) {
    const holdAmount = refundRemainder
      ? parseFloat(this.depositPayment.amount) - this.remainderCost
      : parseInt(this.depositPayment.amount).toFixed(2) || 'unknown';
    this.dialogService
      .refundTransactionDialog(
        this.depositPayment,
        refundRemainder ? this.remainderCost : 0,
        (values) => this.refundDepositValidator(values),
        ({ amount }) => this.processHold(amount),
        {
          modalTitle: 'Refund Deposit',
          showDescription: false,
          refundDetails: [
            {
              key: 'Hold amount',
              value: holdAmount,
            },
            {
              key: 'Transaction id',
              value: this.depositPayment.paymentIntentId,
              onClick: () =>
                this.goToStripePaymentIntent(
                  this.depositPayment.paymentIntentId,
                ),
            },
          ],
          refundButtonText: 'Refund',
        },
      )
      .subscribe((refunded) => {
        if (refunded) {
          this.depositPayment.refundAmount = this.remainderCost.toString();
          this.depositPayment.refunded = true;
          this.calculateRemainderCost();
        }
      });
  }

  chargePaymentMethod() {
    this.dialogService
      .SecurityHoldPostprocessingDialog(
        this.depositPayment,
        false,
        this.charter,
      )
      .subscribe((paymentIntent) => {
        if (paymentIntent) {
          this.appMessagingService.openSnack(
            'Payment method charged successfully!',
          );
        }
      });
  }

  openFailedHoldDialog(failureReason: string) {
    const failureSub = this.dialogService
      .HoldProcessingFailure(
        failureReason,
        this.charter.userId,
        this.depositAmount,
        this.depositPayment.paymentIntentId,
      )
      .subscribe((sendEmail) => {});
    this.subs.push(failureSub);
  }

  getDepositStatus() {
    let status = '';
    if (this.depositPayment.transactions?.status === 'succeeded') {
      status = 'success';
    } else if (this.depositPayment.transactions?.status === 'failed') {
      status = 'failure';
    }
    return status;
  }
}

export interface CharterPayoutRequest {
  charterId: number;
  captainChangeId: number;
  durationChangeValue: number;
  crewMemberIds: number[];
  createFromScratch: boolean;
  date: string;
}
